import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import {
  BaseProviderType,
  OptionType,
  PermissionProviderType,
  StatusByCampaignModel,
  StatusGroupModel,
  StatusModel,
} from "models";
import { useProfile } from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as StatusService from "services/status";
import { aytyFormatError, equalObjects } from "utils";

type FilterType = {
  name: string;
  campaignType: number | string;
  statusGroup: number[];
  status: 0 | 1 | -1;
  isAvailable: 0 | 1 | -1;
};

type StatusContextType = {
  loading: boolean;
  filter: FilterType;
  hasFilter: boolean;
  status?: StatusModel[];
  activeStatus?: StatusModel[];
  statusGroups?: StatusGroupModel[];
  activeStatusGroups?: StatusGroupModel[];
  statusByCampaign?: StatusByCampaignModel[];
  getStatus: () => Promise<void>;
  getStatusByCampaign: (idCampaignType: number) => Promise<void>;
  onChangeFilter: (filter: keyof FilterType, value: unknown) => void;
  onResetFilter: () => void;
};

export const availableStatusOption: OptionType<0 | 1 | -1>[] = [
  { value: 0, label: "labels.all" },
  { value: 1, label: "labels.related" },
  { value: -1, label: "labels.unrelated" },
];

const StatusContext = createContext({} as StatusContextType);

const initialFilter: FilterType = {
  name: "",
  campaignType: "",
  statusGroup: [],
  status: 1,
  isAvailable: 0,
};

const Provider = ({ children }: BaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>({ ...initialFilter });
  const [status, setStatus] = useState<StatusModel[]>();
  const [statusGroups, setStatusGroups] = useState<StatusGroupModel[]>();
  const [statusByCampaign, setStatusByCampaign] =
    useState<StatusByCampaignModel[]>();
  const { pIdProject } = useProfile();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const hasFilter = useMemo(
    () => !equalObjects(initialFilter, filter),
    [filter],
  );

  const activeStatus = useMemo(
    () => status?.filter((c) => c.IsActive),
    [status],
  );

  const activeStatusGroups = useMemo(
    () => statusGroups?.filter((c) => c.IsActive),
    [statusGroups],
  );

  const getStatus = useCallback(async () => {
    setLoading(true);
    if (pIdProject)
      await Promise.all([
        StatusService.getStatus({ pIdProject }).then(async ({ data }) => {
          if (data.idReturnAPI > 0)
            setStatus(
              data.statusList.map((s) => ({
                ...s,
                CodeStatusCrmToPredictive: Number(s.CodeStatusCrmToPredictive), // ToDo: Alterar o Código do discador para number na API
              })),
            );
          else warning({ description: t("errors.failedToFetchList") });
        }),
        StatusService.getStatusGroup({ pIdProject }).then(async ({ data }) => {
          if (data.idReturnAPI > 0) setStatusGroups(data.statusGroupList);
          else warning({ description: t("errors.failedToFetchList") });
        }),
      ]).catch(errorsResolver.defaultError);
    setLoading(false);
  }, [pIdProject, errorsResolver, warning, t]);

  const getStatusByCampaign = useCallback(
    async (idCampaignType: number) => {
      setLoading(true);
      if (pIdProject)
        await StatusService.getStatusByCampaign({
          pIdProject,
          pIdCampaingType: idCampaignType,
        })
          .then(({ data }) => {
            if (data.idReturnAPI > 0)
              setStatusByCampaign(data.statusSystemContextList);
            else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);
      setLoading(false);
    },
    [pIdProject, errorsResolver, warning, t],
  );

  const onChangeFilter = useCallback(
    (filter: keyof FilterType, value: unknown) => {
      setFilter((current) => ({ ...current, [filter]: value }));
      if (filter === "campaignType" && value)
        getStatusByCampaign(value as number);
    },
    [getStatusByCampaign],
  );

  const onResetFilter = useCallback(() => {
    setFilter({ ...initialFilter });
    setStatusByCampaign(undefined);
  }, []);

  useEffect(() => {
    getStatus();
  }, [getStatus]);

  return (
    <StatusContext.Provider
      value={{
        loading,
        filter,
        hasFilter,
        status,
        activeStatus,
        statusGroups,
        activeStatusGroups,
        statusByCampaign,
        onChangeFilter,
        onResetFilter,
        getStatus,
        getStatusByCampaign,
      }}
    >
      {children}
    </StatusContext.Provider>
  );
};

export const StatusProvider = ({
  hasPermission = true,
  ...args
}: PermissionProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useStatus = () => useContext(StatusContext);
